import { ActionTree, MutationTree } from "vuex";
import { searchableMappers, searchResultMappers } from "./GlobalSearchMappers";
import {
  ISearchable,
  ISearchResult,
  ISearchResults,
  LocallySearchableAsset,
  IRootState,
} from "@iloq/shared";
import { handleCommonApiErrors } from "@/api/CommonApiErrorHandler";

interface IInternalSearchResult extends ISearchResult {
  score: number;
}

export enum SearchResultAsset {
  product = "product",
  supportArticle = "supportArticle",
  marketingMaterial = "marketingMaterial",
  newsAndBulletin = "newsAndBulletin",
  event = "event",
}

type GlobalSearchStateSearchableAssets = {
  // Searchable assets are cached here for those assets whose search is handled in the frontend side
  [key in LocallySearchableAsset]: ISearchable[];
};

export type GlobalSearchStateResults = {
  [key in SearchResultAsset]: ISearchResults;
};

export interface IGlobalSearchState {
  searchTerm: string;
  inSearchResultsPage: boolean;
  isOverlayVisible: boolean;
  searchableAssets: GlobalSearchStateSearchableAssets;
  searchResults: GlobalSearchStateResults;
}

const largeListLimit = 100;
const smallListLimitForProducts = 5;
const smallListLimitForOthers = 7;
const emptySearchResult: ISearchResults = {
  count: 0,
  requestedCount: 0,
  smallListLimit: smallListLimitForOthers,
  data: [],
};

const emptySearchResults: GlobalSearchStateResults = {
  [SearchResultAsset.product]: { ...emptySearchResult, smallListLimit: smallListLimitForProducts },
  [SearchResultAsset.supportArticle]: emptySearchResult,
  [SearchResultAsset.marketingMaterial]: emptySearchResult,
  [SearchResultAsset.newsAndBulletin]: emptySearchResult,
  [SearchResultAsset.event]: emptySearchResult,
};

const initialState: IGlobalSearchState = {
  searchTerm: "",
  isOverlayVisible: false,
  inSearchResultsPage: false,
  searchableAssets: {
    [LocallySearchableAsset.news]: [],
    [LocallySearchableAsset.blogpost]: [],
    [LocallySearchableAsset.bulletin]: [],
    [LocallySearchableAsset.material]: [],
    [LocallySearchableAsset.event]: [],
  },
  searchResults: { ...emptySearchResults },
};

export const search = (searchTerm: string, items: ISearchable[], size: number): ISearchResults => {
  const terms = searchTerm.split(/\s+/);
  if (terms.length === 0) {
    return emptySearchResult;
  }

  const result: IInternalSearchResult[] = [];
  items.forEach((item) => {
    let score = 0;
    item.searchableData.forEach((data) => {
      let dataScore = 0;
      let termCount = 0;
      terms.forEach((term) => {
        const matchCount = (data.content.match(new RegExp(term, "gi")) || []).length;
        if (matchCount > 0) {
          // Dampen the effect of term repetition in data using geometric sum
          const repetitiveTermMaxEffect = 4.0;
          const q = 1 - 1 / repetitiveTermMaxEffect;
          dataScore += ((1 - Math.pow(q, matchCount)) / (1 - q)) * data.weight; // More occurrences of single term --> better score
          termCount++;
        }
      });
      score += dataScore * termCount; // More different terms detected --> better score
    });
    if (score > 0) {
      result.push({ ...item.result, score });
    }
  });
  result.sort((r1, r2) => r2.score - r1.score);

  return {
    count: result.length,
    requestedCount: size,
    smallListLimit: smallListLimitForOthers,
    data: result.slice(0, size),
  };
};

const actions: ActionTree<IGlobalSearchState, IRootState> = {
  updateSearchTerm({ commit, dispatch }, searchTerm: string): void {
    commit("setSearchTerm", searchTerm);
    dispatch("search");
  },
  updateSearchTermWithoutSearching({ commit, dispatch }, searchTerm: string): void {
    commit("setSearchTerm", searchTerm);
  },
  async search({ commit, dispatch, state }): Promise<void> {
    if (state.searchTerm === "") {
      commit("clearAllSearchResults");
      return;
    }

    commit("setOverlayVisibility", true);

    commit("setSearchResults", {
      assetType: SearchResultAsset.product,
      results: { searching: true, count: 0, data: [] },
    });
    commit("setSearchResults", {
      assetType: SearchResultAsset.supportArticle,
      results: { searching: true, count: 0, data: [] },
    });

    const large = state.inSearchResultsPage;
    const runProductSearch = async () => {
      const requestedCount = large ? largeListLimit : smallListLimitForProducts;
      try {
        const result = await dispatch(
          "productSearch/searchProductsQuick",
          {
            term: state.searchTerm,
            top: requestedCount,
          },
          { root: true },
        );
        commit("setSearchResults", {
          assetType: SearchResultAsset.product,
          results: searchResultMappers.product(result, requestedCount, smallListLimitForProducts),
        });
      } catch (e) {
        handleCommonApiErrors(e as Error);
      }
    };
    const runSupportArticleSearch = async () => {
      const requestedCount = large ? largeListLimit : smallListLimitForOthers;
      try {
        const result = await dispatch(
          "solutions/searchArticlesQuick",
          {
            term: state.searchTerm,
            top: requestedCount,
          },
          { root: true },
        );
        commit("setSearchResults", {
          assetType: SearchResultAsset.supportArticle,
          results: searchResultMappers.supportArticle(
            result,
            requestedCount,
            smallListLimitForOthers,
          ),
        });
      } catch (e) {
        handleCommonApiErrors(e as Error);
      }
    };
    const runLocalSearches = async () => {
      commit("setSearchResults", {
        assetType: SearchResultAsset.marketingMaterial,
        results: search(
          state.searchTerm,
          state.searchableAssets.material,
          large ? largeListLimit : smallListLimitForOthers,
        ),
      });
      commit("setSearchResults", {
        assetType: SearchResultAsset.event,
        results: search(
          state.searchTerm,
          state.searchableAssets.event,
          large ? largeListLimit : smallListLimitForOthers,
        ),
      });
      commit("setSearchResults", {
        assetType: SearchResultAsset.newsAndBulletin,
        results: search(
          state.searchTerm,
          [
            ...state.searchableAssets.news,
            ...state.searchableAssets.blogpost,
            ...state.searchableAssets.bulletin,
          ],
          large ? largeListLimit : smallListLimitForOthers,
        ),
      });
    };
    await Promise.allSettled([runProductSearch(), runSupportArticleSearch(), runLocalSearches()]);
  },
  async reSearchIfSmallResults({ dispatch, state }): Promise<void> {
    if (state.searchResults.event.requestedCount < largeListLimit) {
      dispatch("search");
    }
  },
  refreshData(
    { commit, dispatch },
    { assetType, assets }: { assetType: LocallySearchableAsset; assets: any[] },
  ): void {
    // Note: if assets that can increase or change during session will be added to Global Search,
    // refreshData() needs to be called after every data change
    commit("setSearchableAssets", {
      assetType,
      assets: searchableMappers[assetType](assets, dispatch),
    });
  },
};

const mutations: MutationTree<IGlobalSearchState> = {
  setSearchTerm(state: IGlobalSearchState, searchTerm: string): void {
    state.searchTerm = searchTerm.trim();
  },
  setInSearchResultPage(state: IGlobalSearchState, value: boolean): void {
    state.inSearchResultsPage = value;
    if (value) {
      state.isOverlayVisible = false;
    }
  },
  setOverlayVisibility(state: IGlobalSearchState, isVisible: boolean): void {
    if (!state.inSearchResultsPage) {
      state.isOverlayVisible = isVisible;
    }
  },
  toggleOverlayVisibilityOnMobile(state: IGlobalSearchState): void {
    if (!state.inSearchResultsPage) {
      state.isOverlayVisible = !state.isOverlayVisible;
    }
    if (state.isOverlayVisible) {
      setTimeout(() => {
        document.getElementById("globalSearchInputForMobile")!.focus();
      }, 100);
    }
  },
  setSearchableAssets(
    state: IGlobalSearchState,
    { assetType, assets }: { assetType: LocallySearchableAsset; assets: ISearchable[] },
  ): void {
    state.searchableAssets[assetType] = assets;
  },
  setSearchResults(
    state: IGlobalSearchState,
    { assetType, results }: { assetType: SearchResultAsset; results: ISearchResults },
  ): void {
    state.searchResults[assetType] = results;
  },
  clearAllSearchResults(state: IGlobalSearchState): void {
    state.searchResults = { ...emptySearchResults };
  },
};

export const globalSearchModule = {
  actions,
  mutations,
  state: initialState,
  namespaced: true,
};
